import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "../../lib/api";

const SESSION_KEY = "rok_assistant_session";

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now() + Math.random());
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export function useAssistant() {
  const sessionId = useRef(getSessionId());
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [loadedHistory, setLoadedHistory] = useState(false);
  const [language, setLanguageState] = useState(localStorage.getItem("rok_assistant_lang") || "en");

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
    localStorage.setItem("rok_assistant_lang", lang);
  }, []);

  useEffect(() => {
    api.get("/assistant/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const loadHistory = useCallback(() => {
    if (loadedHistory) return;
    setLoadedHistory(true);
    api
      .get(`/assistant/history/${sessionId.current}`)
      .then(({ data }) => {
        if (data && data.length) setMessages(data.map((m) => ({ role: m.role, content: m.content })));
      })
      .catch(() => {});
  }, [loadedHistory]);

  const _consumeStream = useCallback(async (res) => {
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop();
      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith("data:")) continue;
        const data = line.slice(5).trim();
        if (data === "[DONE]") continue;
        try {
          const obj = JSON.parse(data);
          if (obj.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
              return copy;
            });
          } else if (obj.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: obj.error };
              return copy;
            });
          }
        } catch {}
      }
    }
  }, []);

  const send = useCallback(async (text) => {
    const message = String(text || "").trim();
    if (!message || streaming) return;
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message, language }),
      });
      await _consumeStream(res);
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection issue. Please try again or call us directly." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  }, [streaming, language, _consumeStream]);

  const analyzeFile = useCallback(async (file) => {
    if (!file || streaming) return;
    setMessages((m) => [...m, { role: "user", content: `📎 ${file.name}` }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("session_id", sessionId.current);
      fd.append("language", language);
      const res = await fetch(`${API}/assistant/analyze-file`, { method: "POST", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: err.detail || "Could not read that file." };
          return copy;
        });
        return;
      }
      await _consumeStream(res);
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Upload failed. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  }, [streaming, language, _consumeStream]);

  const pushSystem = useCallback((content) => {
    setMessages((m) => [...m, { role: "assistant", content }]);
  }, []);

  return { sessionId: sessionId.current, config, messages, streaming, send, analyzeFile, loadHistory, pushSystem, language, setLanguage };
}
